

import { SocialProfile, PokemonCard, GoogleUser, ChatMessage } from '../types';
import { db } from './firebase';
import { collection, getDocs, doc, setDoc, addDoc, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';

// Mock avatars for simulation mode - PRODUCTION: EMPTY
const MOCK_PROFILES: SocialProfile[] = [];

// Cache the mock users so ownership remains consistent during session
let cachedProfiles: SocialProfile[] | null = null;

export interface NotificationItem {
    id: string;
    type: 'message' | 'trade' | 'system';
    title: string;
    body: string;
    timestamp: number;
    read: boolean;
    senderId?: string; // Add senderId to link back to chat
}

export const socialService = {

    async getCommunityFeed(): Promise<SocialProfile[]> {
        let firebaseProfiles: SocialProfile[] = [];

        // 1. Try Fetching from Firebase Cloud
        if (db) {
            try {
                const querySnapshot = await getDocs(collection(db, "users"));
                querySnapshot.forEach((doc) => {
                    const data = doc.data() as SocialProfile;
                    if (data && !isNaN(data.vaultValue)) {
                        firebaseProfiles.push(data);
                    }
                });
            } catch (e) {
                console.warn("Firebase Read Error (Offline Mode Active):", e);
            }
        }

        // 2. Fetch Local Registry (Real Users on Device, Offline fallback)
        const registryRaw = localStorage.getItem('pokebase_registry');
        const localUsers: SocialProfile[] = registryRaw ? JSON.parse(registryRaw) : [];

        // 3. Generate Mock Users (Simulated Community) if cache empty
        if (!cachedProfiles) {
            cachedProfiles = [...MOCK_PROFILES];
        }

        // 4. Combine: Firebase > Local > Mocks
        const combined = [...firebaseProfiles, ...localUsers, ...cachedProfiles];

        // Deduplicate based on ID
        const unique = new Map();
        combined.forEach(p => {
            if (!unique.has(p.id)) {
                unique.set(p.id, p);
            }
        });

        const finalProfiles = Array.from(unique.values());

        // 5. Sort by Value to determine Rank (but keep streamers in mind for UI logic elsewhere)
        finalProfiles.sort((a, b) => b.vaultValue - a.vaultValue);
        finalProfiles.forEach((p, index) => p.rank = index + 1);

        return finalProfiles;
    },

    async getUserById(id: string): Promise<SocialProfile | undefined> {
        // Optimization: Try Direct Fetch First
        if (db) {
            try {
                const docRef = doc(db, "users", id);
                const { getDoc } = await import('firebase/firestore'); // Dynamic import to avoid top-level issues if not needed or add to top
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    return snap.data() as SocialProfile;
                }
            } catch {
                // If fails, fall back to existing logic (maybe offline)
            }
        }

        const all = await this.getCommunityFeed();
        return all.find(p => p.id === id);
    },

    async getLeaderboard(): Promise<SocialProfile[]> {
        return this.getCommunityFeed();
    },

    async getActiveStreams(): Promise<SocialProfile[]> {
        const all = await this.getCommunityFeed();
        return all.filter(p => p.isStreaming);
    },

    // Returns a list of users who own a specific card ID
    async getOwnersOfCard(cardId: string): Promise<SocialProfile[]> {
        const profiles = await this.getCommunityFeed();
        // Simple matching
        return profiles.filter(p => p.vault.some(c => c.id === cardId));
    },

    async syncUserProfile(user: GoogleUser & { isPublic: boolean }, favorites: PokemonCard[]) {
        // Calculate Total Value
        const currentVal = favorites.reduce((sum, card) => {
            const prices = card.tcgplayer?.prices;
            if (!prices) return sum;
            const marketPrice = prices.holofoil?.market ||
                prices.normal?.market ||
                prices.reverseHolofoil?.market ||
                prices['1stEditionHolofoil']?.market || 0;
            const qty = card.quantity || 1;
            const cond = card.condition || 9;
            let multiplier = 0.25;
            if (cond >= 10) multiplier = 1.5;
            else if (cond >= 9) multiplier = 1.0;
            else if (cond >= 7) multiplier = 0.85;
            else if (cond >= 5) multiplier = 0.7;
            else if (cond >= 3) multiplier = 0.5;
            return sum + (marketPrice * qty * multiplier);
        }, 0);

        const userProfile: SocialProfile = {
            id: user.id,
            name: user.fullName,
            avatar: user.avatar,
            tagline: "Certified Collector",
            isOnline: true,
            vaultValue: currentVal,
            rank: 0,
            vault: favorites,
            // Preserves streaming status if user is currently streaming locally
            isStreaming: false,
            walletAddress: user.walletAddress,
            subscriptionTier: user.subscriptionTier
        };

        // 1. Attempt Cloud Sync
        if (db) {
            try {
                if (user.isPublic) {
                    await setDoc(doc(db, "users", user.id), userProfile);
                }
            } catch (e) {
                console.error("Cloud Sync Failed", e);
            }
        }

        // 2. Local Backup (Offline Mode)
        const registryRaw = localStorage.getItem('pokebase_registry');
        let registry: SocialProfile[] = registryRaw ? JSON.parse(registryRaw) : [];
        registry = registry.filter(p => p.id !== user.id);
        if (user.isPublic) {
            registry.push(userProfile);
        }
        localStorage.setItem('pokebase_registry', JSON.stringify(registry));
    },

    async subscribeToPro(): Promise<boolean> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(true);
            }, 1500);
        });
    },

    // --- NOTIFICATIONS & ALERTS ---

    // Request browser notification permission
    async requestNotificationPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            console.warn('[Notifications] Browser does not support notifications');
            return false;
        }
        if (Notification.permission === 'granted') return true;
        if (Notification.permission === 'denied') return false;

        const permission = await Notification.requestPermission();
        return permission === 'granted';
    },

    // Fire a real browser notification (only when tab is not visible)
    _fireBrowserNotification(title: string, body: string, onClick?: () => void) {
        if (!('Notification' in window) || Notification.permission !== 'granted') return;
        if (!document.hidden) return; // Only notify when tab is in background

        try {
            const notification = new Notification(title, {
                body,
                icon: 'https://images.pokemontcg.io/sv8/symbol.png',
                badge: 'https://images.pokemontcg.io/sv8/symbol.png',
                tag: 'pokebase-message', // Prevents stacking duplicate notifications
                requireInteraction: false,
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
                onClick?.();
            };

            // Auto-close after 5 seconds
            setTimeout(() => notification.close(), 5000);
        } catch (e) {
            console.warn('[Notifications] Failed to create notification:', e);
        }
    },

    // Simulates a notification stream for the demo
    subscribeToNotifications(_userId: string, _callback: (unreadCount: number, latest: NotificationItem | null) => void) {
        // PRODUCTION: Disabled mock notifications
        return () => { };
    },

    // --- CHAT SYSTEM ---

    getConversationId(userId1: string, userId2: string) {
        return [userId1, userId2].sort().join('_');
    },

    async getInbox(currentUserId: string): Promise<{ profile: SocialProfile, lastMessage: ChatMessage, unread: number }[]> {
        const allProfiles = await this.getCommunityFeed();
        const inbox: { profile: SocialProfile, lastMessage: ChatMessage, unread: number }[] = [];
        const processedChatIds = new Set<string>();

        // 1. Check Firestore for real conversations
        if (db) {
            try {
                // Query ALL chats where user is a participant
                // We query by participants and sort in memory to avoid index complexity for now
                const q = query(
                    collection(db, "chats"),
                    where("participants", "array-contains", currentUserId)
                );

                const snapshot = await getDocs(q);

                snapshot.forEach(docSnap => {
                    const chatData = docSnap.data();
                    const chatId = docSnap.id;

                    if (processedChatIds.has(chatId)) return;

                    // Identify Partner
                    const partnerId = chatData.participants?.find((id: string) => id !== currentUserId)
                        || chatData.participantIds?.find((id: string) => id !== currentUserId);

                    if (partnerId) {
                        const profile = allProfiles.find(p => p.id === partnerId);

                        // Use lastMessage from parent doc if available (from new sendMessage)
                        // OR fallback to fetching if critical (but let's rely on new writes for now)
                        if (profile && chatData.lastMessage) {
                            inbox.push({
                                profile,
                                lastMessage: chatData.lastMessage as ChatMessage,
                                unread: 0 // TODO: Real unread count from subcollection count or metadata
                            });
                            processedChatIds.add(chatId);
                        }
                        // Compatibility: If no lastMessage on parent, maybe fetch from subcollection?
                        // Skipping for performance now, assuming new chats will work.
                    }
                });

            } catch (e) {
                console.warn("Failed to fetch Firestore chats:", e);
            }
        }

        // 2. Also check localStorage mock conversations (fallback/offline)
        for (const profile of allProfiles) {
            if (profile.id === currentUserId) continue;

            const chatId = this.getConversationId(currentUserId, profile.id);
            if (processedChatIds.has(chatId)) continue; // Already have from Firestore

            const key = `mock_chat_${chatId}`;
            const raw = localStorage.getItem(key);

            if (raw) {
                const msgs: ChatMessage[] = JSON.parse(raw);
                if (msgs.length > 0) {
                    const last = msgs[msgs.length - 1];
                    inbox.push({
                        profile: profile,
                        lastMessage: last,
                        unread: 0
                    });
                    processedChatIds.add(chatId);
                }
            }
        }

        // Sort by recency
        return inbox.sort((a, b) => b.lastMessage.timestamp - a.lastMessage.timestamp);
    },

    // Track conversation partners for inbox loading
    trackConversationPartner(currentUserId: string, partnerId: string) {
        const key = `pokebase_chat_partners_${currentUserId}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        if (!existing.includes(partnerId)) {
            existing.push(partnerId);
            localStorage.setItem(key, JSON.stringify(existing));
        }
    },

    subscribeToChat(conversationId: string, callback: (messages: ChatMessage[]) => void) {
        if (db) {
            const q = query(
                collection(db, "chats", conversationId, "messages"),
                orderBy("timestamp", "asc"),
                limit(50)
            );
            return onSnapshot(q, (snapshot) => {
                const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ChatMessage));
                callback(msgs);
            }, (error) => {
                console.warn("Chat Subscription Error (Offline?)", error);
                // Fallback to offline on error
                this._subscribeOffline(conversationId, callback);
            });
        } else {
            return this._subscribeOffline(conversationId, callback);
        }
    },

    _subscribeOffline(conversationId: string, callback: (messages: ChatMessage[]) => void) {
        const key = `mock_chat_${conversationId}`;

        const load = () => {
            const mockMsgs: ChatMessage[] = JSON.parse(localStorage.getItem(key) || '[]');
            if (mockMsgs.length === 0) {
                // Optional: Start empty or with system message
            }
            callback(mockMsgs);
        };

        load();

        // Listen for local updates (Custom Event)
        const handler = (e: CustomEvent) => callback(e.detail);
        window.addEventListener(`mock_chat_update_${conversationId}` as any, handler);

        return () => window.removeEventListener(`mock_chat_update_${conversationId}` as any, handler);
    },

    async sendMessage(conversationId: string, senderId: string, text: string) {
        const msg = {
            senderId,
            text,
            timestamp: Date.now(),
            isSystem: false
        };

        if (db) {
            try {
                // 1. Add message to subcollection
                await addDoc(collection(db, "chats", conversationId, "messages"), msg);

                // 2. Update/Create parent document for Inbox sync
                // conversationId is "uid1_uid2" (sorted)
                const participants = conversationId.split('_');

                const chatRef = doc(db, "chats", conversationId);
                await setDoc(chatRef, {
                    participants: participants,
                    participantIds: participants, // Keeping both for compatibility with rules
                    lastMessage: msg,
                    updatedAt: Date.now()
                }, { merge: true });

            } catch (e) {
                console.error("Firestore Write Failed - Falling back to local", e);
                this._sendOffline(conversationId, msg);
            }
        } else {
            this._sendOffline(conversationId, msg);
        }
    },

    _sendOffline(conversationId: string, msg: any) {
        const key = `mock_chat_${conversationId}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        // Check if message already exists (deduplication for simulated notifications)
        const exists = existing.some((m: ChatMessage) => m.text === msg.text && Math.abs(m.timestamp - msg.timestamp) < 2000);

        if (!exists) {
            const newMsg = { ...msg, id: Math.random().toString(36).substr(2, 9) };
            const updated = [...existing, newMsg];
            localStorage.setItem(key, JSON.stringify(updated));

            // Dispatch event for local subscribers
            window.dispatchEvent(new CustomEvent(`mock_chat_update_${conversationId}`, { detail: updated }));
        }
    }
};
